/**
 * Two Thousand — Alpine stores + boot.
 * Registered BEFORE alpine.min.js loads, using the `alpine:init` event.
 */
document.addEventListener('alpine:init', () => {
  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  /* -----------------------------------------------------------
     $store.ui — open/close for drawers, mobile nav, modals
     ----------------------------------------------------------- */
  Alpine.store('ui', {
    cartOpen: false,
    navOpen: false,
    notifyOpen: false,
    openCart() { this.cartOpen = true; this._lock(); },
    closeCart() { this.cartOpen = false; this._unlock(); },
    toggleNav() { this.navOpen = !this.navOpen; },
    closeNav() { this.navOpen = false; },
    openNotify() { this.notifyOpen = true; },
    closeNotify() { this.notifyOpen = false; },
    _lock() { document.documentElement.setAttribute('data-scroll-lock', ''); },
    _unlock() {
      if (!this.cartOpen && !this.navOpen && !this.notifyOpen) {
        document.documentElement.removeAttribute('data-scroll-lock');
      }
    },
  });

  /* -----------------------------------------------------------
     $store.cart — cart state synced with Shopify Ajax API
     ----------------------------------------------------------- */
  Alpine.store('cart', {
    items: [],
    itemCount: 0,
    totalPrice: 0,
    currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
    loading: false,

    async init() {
      /* Limpiar cupones persistidos: Shopify guarda los códigos aplicados via
         ?discount= URL como cookie/cart-attribute que sobrevive entre sesiones.
         Esto significa que si el user llega desde el email con CART10, el
         cupón se queda para siempre en futuros visits. Lo limpiamos en cada
         boot, EXCEPTO si el URL actual tiene ?discount= (para respetar los
         links de emails/promociones nuevos). */
      try {
        const hasDiscountInUrl = new URLSearchParams(location.search).has('discount');
        if (!hasDiscountInUrl) {
          await fetch('/discount/clear', { method: 'GET', credentials: 'same-origin' }).catch(() => {});
          document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        }
      } catch (e) { /* silent — falla suave si el endpoint no existe */ }
      await this.refresh();
    },

    async refresh() {
      this.loading = true;
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const data = await res.json();
        this._hydrate(data);
      } finally { this.loading = false; }
    },

    async add(variantId, quantity = 1, properties = {}, openDrawer = true) {
      this.loading = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity, properties }),
        });
        if (!res.ok) throw new Error('Add to cart failed');
        await this.refresh();
        if (openDrawer) Alpine.store('ui').openCart();
      } finally { this.loading = false; }
    },

    async change(key, quantity) {
      this.loading = true;
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        const data = await res.json();
        this._hydrate(data);
      } finally { this.loading = false; }
    },

    remove(key) { return this.change(key, 0); },
    increment(key, currentQty) { return this.change(key, currentQty + 1); },
    decrement(key, currentQty) { return this.change(key, Math.max(0, currentQty - 1)); },

    format(cents) { return money(cents, this.currency); },

    _hydrate(data) {
      this.items = data.items || [];
      this.itemCount = data.item_count || 0;
      this.totalPrice = data.total_price || 0;
    },
  });

  /* -----------------------------------------------------------
     $store.gradient — controls the animated canvas background
     ----------------------------------------------------------- */
  Alpine.store('gradient', {
    setFromHex(hex, weight = 0.8, immediate = false) {
      if (window.__twoThousandGradient && typeof window.__twoThousandGradient.setColor === 'function') {
        window.__twoThousandGradient.setColor(hex, weight, immediate);
      } else {
        // Fallback: update CSS var if canvas engine isn't ready yet
        document.documentElement.style.setProperty('--gradient-c1', hex);
      }
    },
    reset() {
      // Delegate to gradient engine's soft reset — restores the CSS-var
      // palette WITHOUT punchify, so we go back to the tenue resting state.
      if (window.__twoThousandGradient && window.__twoThousandGradient.resetColor) {
        window.__twoThousandGradient.resetColor();
      }
    },
  });
});

/* Money helper exposed for non-Alpine code */
window.TT = window.TT || {};
window.TT.money = (cents, currency) => {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
    }).format(cents / 100);
  } catch (e) { return `$${(cents / 100).toFixed(2)}`; }
};
